import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const DEFAULT_CANVAS_OPTIONS = {
  scale: 2,
  useCORS: true,
  backgroundColor: '#ffffff',
};

export const buildPdfFromElement = async (element, options = {}) => {
  if (!element) {
    throw new Error('PDF source element is required');
  }

  const marginMm = Number.isFinite(options.marginMm) ? options.marginMm : 0;
  const orientation = options.orientation || 'p';
  const format = options.format || 'a4';

  const canvas = await html2canvas(element, {
    ...DEFAULT_CANVAS_OPTIONS,
    ...(options.canvasOptions || {}),
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF(orientation, 'mm', format);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (marginMm * 2);
  const contentHeight = pageHeight - (marginMm * 2);
  const imageHeight = (canvas.height * contentWidth) / canvas.width;

  let renderedHeight = 0;
  let pageIndex = 0;

  while (renderedHeight < imageHeight - 0.1) {
    if (pageIndex > 0) {
      pdf.addPage();
    }

    const yPosition = marginMm - renderedHeight;
    pdf.addImage(imgData, 'PNG', marginMm, yPosition, contentWidth, imageHeight, undefined, 'FAST');

    renderedHeight += contentHeight;
    pageIndex += 1;
  }

  return pdf;
};

export const saveElementAsPdf = async (element, fileName, options = {}) => {
  const pdf = await buildPdfFromElement(element, options);
  pdf.save(fileName);
  return pdf;
};

export const printElementInWindow = async (element, options = {}) => {
  if (!element) {
    throw new Error('Print source element is required');
  }

  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=980,height=1200');
  if (!printWindow) {
    throw new Error('Unable to open print window');
  }

  const doc = printWindow.document;
  const title = options.title || document.title || 'Invoice';

  const stylesheetHtml = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

  const printCss = `
    <style>
      @page { size: A4; margin: 8mm; }
      html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .print-root { width: 100%; margin: 0; padding: 0; }
      .print-root .invoice-document { margin: 0 !important; padding: 10px 12px !important; }
      .print-root * { break-inside: avoid-page; }
    </style>
  `;

  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8" /><title>${title}</title>${stylesheetHtml}${printCss}</head><body><div class="print-root"></div></body></html>`);
  doc.close();

  const target = doc.querySelector('.print-root');
  if (!target) {
    throw new Error('Print target not found');
  }

  target.appendChild(element.cloneNode(true));

  await new Promise((resolve) => {
    const done = () => resolve();
    if (doc.readyState === 'complete') {
      done();
      return;
    }
    printWindow.addEventListener('load', done, { once: true });
    setTimeout(done, 300);
  });

  printWindow.focus();
  printWindow.print();
  setTimeout(() => {
    try {
      printWindow.close();
    } catch (_) {
      // noop
    }
  }, 300);
};
